import { motion } from "framer-motion";
import { useLanguage } from "../LanguageContext";

export default function PrivacyPolicy() {
  const { t } = useLanguage();
  return (
    <div className="min-h-screen bg-background relative overflow-hidden pt-52">
      <main className="relative">
        {/* Hero Section */}
        <section className="pt-32 pb-20 relative overflow-hidden">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative">
            <motion.div
              className="text-center space-y-8"
              initial={{ opacity: 0, y: 50 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              <motion.div
                className="space-y-6"
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.2 }}
              >
                <h1 className="text-4xl lg:text-6xl leading-tight text-foreground">
                  <span className="bg-gradient-to-r from-slate-800 via-slate-700 to-cyan-600 bg-clip-text text-transparent">
                    {t('privacy.title')}
                  </span>
                </h1>
                <p className="text-lg text-muted-foreground">
                  {t('privacy.lastUpdated')}
                </p>
              </motion.div>
            </motion.div>
          </div>
        </section>

        {/* Content Section */}
        <section className="pb-20 relative overflow-hidden">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative">
            <motion.div
              className="prose prose-slate max-w-none"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
            >
              <div className="bg-white border border-slate-200/60 rounded-xl p-8 lg:p-12 shadow-lg space-y-8">
                <div>
                  <h2 className="text-2xl lg:text-3xl text-foreground mb-4">{t('privacy.introduction')}</h2>
                  <p className="text-muted-foreground leading-relaxed">
                    Z Creative Partners Co., Ltd. ("we," "our," or "us") is committed to protecting your privacy. 
                    This Privacy Policy explains how we collect, use, disclose, and safeguard your information when 
                    you visit our website or use our services.
                  </p>
                </div>

                <div>
                  <h2 className="text-2xl lg:text-3xl text-foreground mb-4">Information We Collect</h2>
                  <div className="space-y-4 text-muted-foreground">
                    <h3 className="text-lg text-foreground">Personal Information</h3>
                    <p>We may collect personal information that you voluntarily provide to us when you:</p>
                    <ul className="list-disc pl-6 space-y-2">
                      <li>Contact us through our website or email</li>
                      <li>Subscribe to our newsletter</li>
                      <li>Request information about our services</li>
                      <li>Participate in surveys or promotions</li>
                    </ul>
                    
                    <h3 className="text-lg text-foreground mt-6">Automatically Collected Information</h3>
                    <p>When you visit our website, we may automatically collect certain information, including:</p>
                    <ul className="list-disc pl-6 space-y-2">
                      <li>IP address and location data</li>
                      <li>Browser type and version</li>
                      <li>Operating system</li>
                      <li>Pages visited and time spent on our site</li>
                      <li>Referring website addresses</li>
                    </ul>
                  </div>
                </div>

                <div>
                  <h2 className="text-2xl lg:text-3xl text-foreground mb-4">How We Use Your Information</h2>
                  <div className="space-y-4 text-muted-foreground">
                    <p>We use the information we collect to:</p>
                    <ul className="list-disc pl-6 space-y-2">
                      <li>Provide and maintain our services</li>
                      <li>Process and respond to your inquiries</li>
                      <li>Send you marketing communications (with your consent)</li>
                      <li>Improve our website and services</li>
                      <li>Comply with legal obligations</li>
                      <li>Protect our rights and prevent fraud</li>
                    </ul>
                  </div>
                </div>

                <div>
                  <h2 className="text-2xl lg:text-3xl text-foreground mb-4">Information Sharing and Disclosure</h2>
                  <div className="space-y-4 text-muted-foreground">
                    <p>We do not sell, trade, or rent your personal information to third parties. We may share your information in the following circumstances:</p>
                    <ul className="list-disc pl-6 space-y-2">
                      <li>With service providers who assist us in operating our website and conducting our business</li>
                      <li>To comply with legal requirements or respond to lawful requests</li>
                      <li>To protect our rights, property, or safety, or that of others</li>
                      <li>In connection with a business transfer or merger</li>
                    </ul>
                  </div>
                </div>

                <div>
                  <h2 className="text-2xl lg:text-3xl text-foreground mb-4">Cookies and Tracking Technologies</h2>
                  <div className="space-y-4 text-muted-foreground">
                    <p>We use cookies and similar tracking technologies to enhance your experience on our website. Cookies are small files stored on your device that help us:</p>
                    <ul className="list-disc pl-6 space-y-2">
                      <li>Remember your preferences</li>
                      <li>Analyze website traffic and usage</li>
                      <li>Provide personalized content</li>
                      <li>Improve our services</li>
                    </ul>
                    <p>You can control cookie settings through your browser preferences.</p>
                  </div>
                </div>

                <div>
                  <h2 className="text-2xl lg:text-3xl text-foreground mb-4">Data Security</h2>
                  <p className="text-muted-foreground leading-relaxed">
                    We implement appropriate technical and organizational measures to protect your personal information 
                    against unauthorized access, alteration, disclosure, or destruction. However, no method of transmission 
                    over the internet or electronic storage is 100% secure.
                  </p>
                </div>

                <div>
                  <h2 className="text-2xl lg:text-3xl text-foreground mb-4">Your Rights</h2>
                  <div className="space-y-4 text-muted-foreground">
                    <p>Depending on your location, you may have the following rights regarding your personal information:</p>
                    <ul className="list-disc pl-6 space-y-2">
                      <li>Right to access and receive a copy of your personal data</li>
                      <li>Right to rectify inaccurate or incomplete data</li>
                      <li>Right to erase your personal data</li>
                      <li>Right to restrict processing of your data</li>
                      <li>Right to data portability</li>
                      <li>Right to object to processing</li>
                      <li>Right to withdraw consent</li>
                    </ul>
                  </div>
                </div>

                <div>
                  <h2 className="text-2xl lg:text-3xl text-foreground mb-4">International Data Transfers</h2>
                  <p className="text-muted-foreground leading-relaxed">
                    As we operate across multiple countries in Southeast Asia, your information may be transferred to 
                    and processed in countries other than your own. We ensure appropriate safeguards are in place to 
                    protect your data during such transfers.
                  </p>
                </div>

                <div>
                  <h2 className="text-2xl lg:text-3xl text-foreground mb-4">Children's Privacy</h2>
                  <p className="text-muted-foreground leading-relaxed">
                    Our services are not intended for individuals under the age of 16. We do not knowingly collect 
                    personal information from children under 16. If you are a parent or guardian and believe your 
                    child has provided us with personal information, please contact us.
                  </p>
                </div>

                <div>
                  <h2 className="text-2xl lg:text-3xl text-foreground mb-4">Changes to This Privacy Policy</h2>
                  <p className="text-muted-foreground leading-relaxed">
                    We may update this Privacy Policy from time to time. We will notify you of any changes by posting 
                    the new Privacy Policy on this page and updating the "Last updated" date. You are advised to review 
                    this Privacy Policy periodically for any changes.
                  </p>
                </div>

                <div className="border-t border-slate-200/60 pt-8">
                  <h2 className="text-2xl lg:text-3xl text-foreground mb-4">Contact Us</h2>
                  <div className="text-muted-foreground space-y-2">
                    <p>If you have any questions about this Privacy Policy, please contact us:</p>
                    <div className="space-y-1">
                      <p><strong>Email:</strong> info@z-creative-partners.com</p>
                      <p><strong>Address:</strong> KS Building 5F, 3-16-12 Roppongi, Minato City, Tokyo, Japan</p>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </section>
      </main>
    </div>
  );
}